import { createSlice } from '@reduxjs/toolkit';
import { checkUser, createUser, getUsers, validateStoredToken } from './thunks';
import { clearAuthStorage, getStoredAuth } from '../../utils/authStorage';

function normCommandPriority(u) {
  if (!u || typeof u !== 'object') return 0;
  return Math.max(
    0,
    Math.min(
      9,
      Math.trunc(
        Number(
          u.commandPriority ?? u.commandpriority ?? u.command_priority ?? 0,
        ) || 0,
      ),
    ),
  );
}

// --- Новый initialState с восстановлением из localStorage/sessionStorage ---
function getInitialAuthState() {
  const storedAuth = getStoredAuth();
  const user = storedAuth?.user || null;
  const token = storedAuth?.token || null;
  if (user && token) {
    return {
      id: user.id,
      result: true,
      role: user.role,
      userName: user.userName,
      // Командный приоритет 0..9. Используется для активации кнопок команд
      // в карточках. Берём из пейлоада логина (см. бэкенд auth.service / project-auth).
      commandPriority: normCommandPriority(user),
      visibleGroups: user.visibleGroups,
        email: user.email || null,
        emailVerified: !!user.emailVerified,
        mustVerifyEmail: !!user.mustVerifyEmail,
        blockLoginUntilEmailVerified: !!user.blockLoginUntilEmailVerified,
      users: [],
      error: null,
      isLoading: false,
    };
  }
  // Если ничего нет — дефолт
  return {
    id: null,
    result: false,
    role: null,
    userName: null,
    commandPriority: 0,
    visibleGroups: [],
    email: null,
    emailVerified: false,
    mustVerifyEmail: false,
    blockLoginUntilEmailVerified: false,
    users: [],
    error: null,
    isLoading: false,
  };
}

const initialState = getInitialAuthState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUserToLocal(state, action) {
      state.id = action.payload.id;
      state.result = action.payload.result;
      state.role = action.payload.role;
      state.userName = action.payload.userName;
      state.commandPriority = normCommandPriority(action.payload);
      state.visibleGroups = action.payload.visibleGroups || [];
      state.email = action.payload.email || null;
      state.emailVerified = !!action.payload.emailVerified;
      state.mustVerifyEmail = !!action.payload.mustVerifyEmail;
      state.blockLoginUntilEmailVerified =
        !!action.payload.blockLoginUntilEmailVerified;
      state.error = null;
      state.isLoading = false;
    },
    setLogOut(state) {
      state.id = null;
      state.result = false;
      state.role = null;
      state.userName = null;
      state.commandPriority = 0;
      state.visibleGroups = [];
      state.email = null;
      state.emailVerified = false;
      state.mustVerifyEmail = false;
      state.blockLoginUntilEmailVerified = false;
      state.users = [];
      state.error = null;
      state.isLoading = false;
      clearAuthStorage();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkUser.fulfilled, (state, action) => {
        const { user } = action.payload;
        state.id = user.id;
        state.result = true;
        state.role = user.role;
        state.userName = user.username;
        state.commandPriority = normCommandPriority(user);
        state.visibleGroups = user.visibleGroups || [];
        state.email = user.email || null;
        state.emailVerified = !!user.emailVerified;
        state.mustVerifyEmail = !!user.mustVerifyEmail;
        state.blockLoginUntilEmailVerified = !!user.blockLoginUntilEmailVerified;
        state.error = null;
        state.isLoading = false;
      })
      .addCase(checkUser.rejected, (state, action) => {
        state.id = null;
        state.result = false;
        state.role = null;
        state.userName = null;
        state.commandPriority = 0;
        state.visibleGroups = [];
        state.email = null;
        state.emailVerified = false;
        state.mustVerifyEmail = false;
        state.blockLoginUntilEmailVerified = false;
        state.error =
          (action.payload && action.payload.message) ||
          action.payload ||
          'Authentication failed';
        state.isLoading = false;
        clearAuthStorage();
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.users = [...state.users, action.payload];
        state.error = null;
        state.isLoading = false;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.error = action.payload || 'Failed to create user';
        state.isLoading = false;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.users = action.payload;
        state.error = null;
        state.isLoading = false;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch users';
        state.isLoading = false;
      })
      .addCase(validateStoredToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(validateStoredToken.fulfilled, (state, action) => {
        const { user } = action.payload;
        state.id = user.id;
        state.result = true;
        state.role = user.role;
        state.userName = user.username;
        state.commandPriority = normCommandPriority(user);
        state.visibleGroups = user.visibleGroups || [];
        state.email = user.email || null;
        state.emailVerified = !!user.emailVerified;
        state.mustVerifyEmail = !!user.mustVerifyEmail;
        state.blockLoginUntilEmailVerified = !!user.blockLoginUntilEmailVerified;
        state.error = null;
        state.isLoading = false;
      })
      .addCase(validateStoredToken.rejected, (state, action) => {
        state.id = null;
        state.result = false;
        state.role = null;
        state.userName = null;
        state.commandPriority = 0;
        state.visibleGroups = [];
        state.email = null;
        state.emailVerified = false;
        state.mustVerifyEmail = false;
        state.blockLoginUntilEmailVerified = false;
        state.error = action.payload || 'Token validation failed';
        state.isLoading = false;
        clearAuthStorage();
      });
  },
});

export const { setUserToLocal, setLogOut } = authSlice.actions;
export default authSlice.reducer;
